import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../contexts/UserContext';
import authenticate from '../../utils/Authenticate';
import displayError from '../../utils/ErrorHandler';

const inputClass = "block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";
const labelClass = "block text-sm font-medium leading-6 text-gray-900";

const EMPTY_FORM = {
  slug: '',
  displayName: '',
  adminUsername: '',
  adminPassword: '',
  adminName: '',
  seedItems: false,
  seedDishes: false,
  seedMenus: false,
  seedSource: 'master',
  sourceCity: '',
};

const CreateCity = () => {
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [cities, setCities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });

  useEffect(() => {
    async function init() {
      if (!user) {
        const u = await authenticate(setUser, navigate);
        if (!u) return;
        if (u.role !== 'SuperAdmin') { navigate('/'); return; }
      } else if (user.role !== 'SuperAdmin') {
        navigate('/');
        return;
      }
      fetchCities();
    }
    init();
  }, []);

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URI}/api/superadmin/cities`, { withCredentials: true });
      if (res.data.success) setCities(res.data.data);
    } catch (_) {}
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSlugChange = (e) => {
    const val = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '');
    setForm((prev) => ({ ...prev, slug: val }));
  };

  const handleSeedSourceChange = (source) => {
    setForm((prev) => ({ ...prev, seedSource: source, sourceCity: '' }));
  };

  const anySeedChecked = form.seedItems || form.seedDishes || form.seedMenus;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.slug || !form.displayName || !form.adminUsername || !form.adminPassword) {
      setNotification({ message: 'Please fill in all required fields.', type: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (anySeedChecked && form.seedSource === 'city' && !form.sourceCity) {
      setNotification({ message: 'Please select a source city to copy from.', type: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const payload = {
      slug: form.slug,
      displayName: form.displayName,
      adminUsername: form.adminUsername,
      adminPassword: form.adminPassword,
      adminName: form.adminName,
      seedItems: form.seedItems,
      seedDishes: form.seedDishes,
      seedMenus: form.seedMenus,
      sourceCity: form.seedSource === 'city' ? form.sourceCity : undefined,
    };

    setSubmitting(true);
    setNotification({ message: '', type: '' });
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/superadmin/cities`,
        payload,
        { withCredentials: true }
      );
      if (res.data.success) {
        setNotification({ message: `City "${form.displayName}" created successfully!`, type: 'success' });
        setForm(EMPTY_FORM);
        fetchCities();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      const msg = displayError(err, navigate, setUser);
      setNotification({ message: msg || 'Failed to create city.', type: 'error' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-full flex-1 flex-col px-6 py-4 lg:px-8">
      <div className="w-full mx-auto md:max-w-lg">

        {notification.message && (
          <div className={`mb-4 p-4 rounded-lg ${notification.type === 'success' ? 'bg-green-200 border-green-600 text-green-600' : 'bg-red-200 border-red-600 text-red-600'}`}>
            {notification.message}
          </div>
        )}

        <form className="space-y-6" onSubmit={handleSubmit}>

          <div>
            <label className={labelClass}>City Slug *</label>
            <div className="mt-2">
              <input
                name="slug"
                value={form.slug}
                onChange={handleSlugChange}
                placeholder="e.g. mumbai"
                className={inputClass}
                required
              />
            </div>
            <p className="mt-1 text-xs text-gray-400 px-2">Lowercase letters and numbers only — used as the database identifier.</p>
          </div>

          <div>
            <label className={labelClass}>Display Name *</label>
            <div className="mt-2">
              <input
                name="displayName"
                value={form.displayName}
                onChange={handleChange}
                placeholder="e.g. Mumbai"
                className={inputClass}
                required
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>City Admin Credentials</label>
          </div>

          <div className="flex flex-col gap-4 px-1">
            <div>
              <label className={labelClass}>Full Name (optional)</label>
              <div className="mt-2">
                <input
                  name="adminName"
                  value={form.adminName}
                  onChange={handleChange}
                  placeholder="Admin full name"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Username *</label>
              <div className="mt-2">
                <input
                  name="adminUsername"
                  value={form.adminUsername}
                  onChange={handleChange}
                  placeholder="Admin username"
                  className={inputClass}
                  required
                />
              </div>
            </div>

            <div>
              <label className={labelClass}>Password *</label>
              <div className="mt-2">
                <input
                  name="adminPassword"
                  type="password"
                  value={form.adminPassword}
                  onChange={handleChange}
                  placeholder="Admin password"
                  className={inputClass}
                  required
                />
              </div>
            </div>
          </div>

          {/* Seed section */}
          <div>
            <label className={labelClass}>Seed Data</label>
            <p className="mt-1 text-xs text-gray-400 px-2">Optionally copy existing data into the new city database on creation.</p>

            {/* What to copy */}
            <div className="mt-3 space-y-3 px-2">
              {[
                { name: 'seedItems', label: 'Items' },
                { name: 'seedDishes', label: 'Dishes' },
                { name: 'seedMenus', label: 'Menus' },
              ].map(({ name, label }) => (
                <label key={name} className="flex items-center gap-3 cursor-pointer">
                  <div className="custom-checkbox">
                    <input
                      type="checkbox"
                      name={name}
                      checked={form[name]}
                      onChange={handleChange}
                    />
                    <span className="checkmark"></span>
                  </div>
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>

            {/* Source toggle — only shown when at least one checkbox is ticked */}
            {anySeedChecked && (
              <div className="mt-4 px-2">
                <p className="text-xs font-medium text-gray-600 mb-2">Copy from</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleSeedSourceChange('master')}
                    className={`flex-1 py-2 rounded-full text-xs font-semibold border transition-colors ${
                      form.seedSource === 'master'
                        ? 'bg-fmb text-white border-fmb'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-fmb'
                    }`}
                  >
                    Master Catalog
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSeedSourceChange('city')}
                    className={`flex-1 py-2 rounded-full text-xs font-semibold border transition-colors ${
                      form.seedSource === 'city'
                        ? 'bg-fmb text-white border-fmb'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-fmb'
                    }`}
                  >
                    Existing City
                  </button>
                </div>

                {form.seedSource === 'city' && (
                  <div className="mt-3">
                    {cities.length === 0 ? (
                      <p className="text-xs text-gray-400 px-1">No existing cities available.</p>
                    ) : (
                      <select
                        name="sourceCity"
                        value={form.sourceCity}
                        onChange={handleChange}
                        className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                      >
                        <option value="">Select a city…</option>
                        {cities.map((c) => (
                          <option key={c.slug} value={c.slug}>
                            {c.displayName} ({c.slug})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={submitting}
              className="flex w-full justify-center rounded-full text-white bg-fmb px-3 py-3 text-sm font-semibold leading-6 shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-60"
            >
              {submitting ? 'CREATING...' : 'CREATE CITY'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default CreateCity;
